import { db } from '../firebase'
import {
  collection, doc, setDoc, updateDoc, getDoc,
  query, where, getDocs, onSnapshot, deleteDoc,
  serverTimestamp, runTransaction, increment,
} from 'firebase/firestore'

// ─── Pure helpers (no Firestore) ─────────────────────────────────────────────

// No 0/O/I/1 to avoid misreads when typing
const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

// crypto.randomUUID() is not available in all environments (e.g. non-HTTPS
// mobile browsers), so we use a Math.random()-based fallback instead.
const generateUUID = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })

export function generateLobbyCode() {
  let code = ''
  for (let i = 0; i < 6; i++) {
    code += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]
  }
  return code
}

export function getOrCreatePlayerId() {
  const key = 'action_player_id'
  let id = localStorage.getItem(key)
  if (!id) {
    id = generateUUID()
    localStorage.setItem(key, id)
  }
  return id
}

// ─── Lobby ───────────────────────────────────────────────────────────────────

/**
 * P1 creates a new lobby. Returns { gameId, code, playerId }.
 * Throws on Firestore error.
 */
export async function createGame(gameType = 'h2h') {
  const code  = generateLobbyCode()
  const p1Id  = getOrCreatePlayerId()
  const gameRef = doc(collection(db, 'games'))

  const docData = {
    code,
    gameType,
    status: 'waiting',
    createdAt: serverTimestamp(),
    p1: { id: p1Id, actor: null, ready: false, cueCardsUsed: 0 },
    p2: { id: null,  actor: null, ready: false, cueCardsUsed: 0 },
    startActor: null,
    goalActor:  null,
    ticketsCost: 1,
  }

  if (gameType === 'coop') {
    docData.chain       = []
    docData.currentTurn = null
    docData.lastMove    = null
    docData.turnData    = { movie: null, actor: null, lockedIn: false }
  }

  try {
    await setDoc(gameRef, docData)
  } catch (err) {
    console.error('createGame setDoc error:', err)
    throw err
  }

  return { gameId: gameRef.id, code, playerId: p1Id }
}

/**
 * Look up a game document by 6-char display code.
 * Returns the game object (with .id) or null if not found.
 */
export async function lookupGameByCode(code) {
  const q = query(
    collection(db, 'games'),
    where('code', '==', code.trim().toUpperCase()),
  )
  const snap = await getDocs(q)
  if (snap.empty) return null
  const d = snap.docs[0]
  return { id: d.id, ...d.data() }
}

/**
 * P2 joins an existing lobby by code.
 * Returns { gameId, code, playerId } on success.
 * Returns { error: 'not_found' | 'already_started' } on failure.
 * Throws on Firestore error.
 */
export async function joinGame(code) {
  const game = await lookupGameByCode(code)
  if (!game) return { error: 'not_found' }
  if (game.status !== 'waiting') return { error: 'already_started' }

  const p2Id = getOrCreatePlayerId()

  await updateDoc(doc(db, 'games', game.id), {
    'p2.id': p2Id,
    status: 'picking',
  })

  return { gameId: game.id, code: game.code, playerId: p2Id, gameType: game.gameType ?? 'h2h' }
}

// ─── Real-time subscription ──────────────────────────────────────────────────

/**
 * Subscribe to real-time updates for a game document.
 * Returns an unsubscribe function.
 */
export function subscribeToGame(gameId, callback) {
  console.log('[subscribeToGame] attaching listener for gameId:', gameId)
  const unsub = onSnapshot(
    doc(db, 'games', gameId),
    (snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() }
        console.log('[subscribeToGame] snapshot received — status:', data.status,
          '| p1.ready:', data.p1?.ready, '| p2.ready:', data.p2?.ready)
        callback(data)
      } else {
        console.warn('[subscribeToGame] doc does not exist for gameId:', gameId)
      }
    },
    (err) => {
      console.error('[subscribeToGame] snapshot error:', err)
    },
  )
  console.log('[subscribeToGame] listener attached, unsub type:', typeof unsub)
  return unsub
}

/**
 * Fetch a single game document by ID (no real-time).
 */
export async function getGame(gameId) {
  const snap = await getDoc(doc(db, 'games', gameId))
  return snap.exists() ? { id: snap.id, ...snap.data() } : null
}

/**
 * Delete a game document (used when P1 abandons before P2 joins).
 */
export async function deleteGame(gameId) {
  await deleteDoc(doc(db, 'games', gameId))
}

// ─── Actor picking (Phase C) ─────────────────────────────────────────────────

/**
 * Lock in an actor for a player. Writes actor snapshot, ready:true, and
 * a client timestamp (ms) used for duplicate-detection tiebreaking.
 * playerKey: 'p1' | 'p2'
 */
export async function lockInActor(gameId, playerKey, actor, lockedAt) {
  await updateDoc(doc(db, 'games', gameId), {
    [`${playerKey}.actor`]:    actor,
    [`${playerKey}.ready`]:    true,
    [`${playerKey}.lockedAt`]: lockedAt,
  })
}

/**
 * Reset a player's lock (duplicate case — the later locker repicks).
 */
export async function resetActorLock(gameId, playerKey) {
  await updateDoc(doc(db, 'games', gameId), {
    [`${playerKey}.actor`]:    null,
    [`${playerKey}.ready`]:    false,
    [`${playerKey}.lockedAt`]: null,
  })
}

/**
 * P1 assigns start/goal actors and advances the game to 'playing'.
 * Only called once both players have locked in different actors.
 * For co-op games, also seeds currentTurn: 'p1' so turn-blocking
 * works from the very first snapshot both players receive.
 */
export async function assignActorsAndStart(gameId, startActor, goalActor, gameType = 'h2h') {
  const update = {
    startActor,
    goalActor,
    status: 'playing',
  }
  if (gameType === 'coop') {
    update.currentTurn = 'p1'
  }
  await updateDoc(doc(db, 'games', gameId), update)
}

// ─── Gameplay (Phase D) ──────────────────────────────────────────────────────

/**
 * Atomically write a player's result and advance game status.
 * Uses a Firestore transaction to safely detect who finishes first.
 *
 * result shape: { hops, timeSeconds, chain }  (chain is trimmed for storage)
 *
 * Returns: { finishedFirst: bool, status: 'waiting_results'|'results', opponentActor }
 */
export async function submitPlayerResult(gameId, playerKey, result) {
  const gameRef = doc(db, 'games', gameId)

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(gameRef)
    if (!snap.exists()) throw new Error('Game document not found')

    const gameData = snap.data()
    const otherKey = playerKey === 'p1' ? 'p2' : 'p1'
    const opponentResult = gameData[otherKey]?.result ?? null

    const finishedFirst = !opponentResult
    const status = opponentResult ? 'results' : 'waiting_results'

    tx.update(gameRef, {
      [`${playerKey}.result`]: { ...result, finishedFirst },
      status,
    })

    return {
      finishedFirst,
      status,
      opponentActor: gameData[otherKey]?.actor ?? null,
    }
  })
}

// ─── Results room (Phase E) ──────────────────────────────────────────────────

/**
 * Requester creates a new game for the rematch.
 * - New game: p1 = requester, p2 = opponent, status = 'waiting_rematch'
 * - Old game gets a `rematch` field pointing to the new doc.
 * Returns { newGameId, code }.
 */
export async function createRematchGame(oldGameId, requesterKey) {
  const oldSnap = await getDoc(doc(db, 'games', oldGameId))
  if (!oldSnap.exists()) throw new Error('Original game not found')
  const oldData = oldSnap.data()

  const opponentKey  = requesterKey === 'p1' ? 'p2' : 'p1'
  const requesterId  = oldData[requesterKey]?.id
  const opponentId   = oldData[opponentKey]?.id

  const code    = generateLobbyCode()
  const gameRef = doc(collection(db, 'games'))

  await setDoc(gameRef, {
    code,
    status: 'waiting_rematch',
    createdAt: serverTimestamp(),
    p1: { id: requesterId, actor: null, ready: false },
    p2: { id: opponentId,  actor: null, ready: false },
    startActor: null,
    goalActor:  null,
    ticketsCost: 1,
    rematchOf: oldGameId,
  })

  await updateDoc(doc(db, 'games', oldGameId), {
    rematch: {
      newGameId: gameRef.id,
      requestedByKey: requesterKey,
      status: 'pending',
    },
  })

  return { newGameId: gameRef.id, code }
}

/**
 * Opponent accepts the rematch.
 * - Old game rematch.status → 'accepted'
 * - New game status → 'picking'
 */
export async function acceptRematch(oldGameId, newGameId) {
  await updateDoc(doc(db, 'games', oldGameId), { 'rematch.status': 'accepted' })
  await updateDoc(doc(db, 'games', newGameId), { status: 'picking' })
}

/**
 * Opponent declines the rematch.
 * - Old game rematch.status → 'declined'
 * - New game deleted
 */
export async function declineRematch(oldGameId, newGameId) {
  await updateDoc(doc(db, 'games', oldGameId), { 'rematch.status': 'declined' })
  try { await deleteDoc(doc(db, 'games', newGameId)) } catch {}
}

// ─── Co-Op gameplay ──────────────────────────────────────────────────────────

/**
 * Set currentTurn: 'p1' on a Co-Op game if not yet assigned.
 * Called once by P1 when entering the gameplay screen.
 */
export async function initCoopGame(gameId) {
  const gameRef = doc(db, 'games', gameId)
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(gameRef)
    if (!snap.exists()) return
    if (snap.data().currentTurn == null) {
      tx.update(gameRef, { currentTurn: 'p1' })
    }
  })
}

/**
 * Atomically submit one Co-Op turn:
 *   1. Read current chain from Firestore
 *   2. Append movieEntry + actorEntry
 *   3. Flip currentTurn to the other player
 *   4. Write lastMove { playerKey, movie, actor, timestamp }
 *   5. Reset turnData
 *   6. If actor.id === goalActorId → set status: 'won'
 */
export async function submitCoopTurn(gameId, playerKey, movie, actor, goalActorId) {
  const gameRef = doc(db, 'games', gameId)
  const otherKey = playerKey === 'p1' ? 'p2' : 'p1'

  console.log(`[submitCoopTurn] playerKey: ${playerKey} | flipping currentTurn: ${playerKey} → ${otherKey}`)

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(gameRef)
    if (!snap.exists()) throw new Error('Game not found')

    const data = snap.data()
    console.log(`[submitCoopTurn] Firestore currentTurn before write: ${data.currentTurn}`)

    const currentChain = data.chain ?? []

    const movieEntry = {
      type: 'movie',
      id: movie.id,
      title: movie.title,
      poster_path: movie.poster_path ?? null,
    }
    const actorEntry = {
      type: 'actor',
      id: actor.id,
      name: actor.name,
      profile_path: actor.profile_path ?? null,
      gender: actor.gender ?? 0,
    }

    const newChain = [...currentChain, movieEntry, actorEntry]
    const isWin = actor.id === goalActorId

    const update = {
      chain: newChain,
      currentTurn: otherKey,
      lastMove: { playerKey, movie: movieEntry, actor: actorEntry, timestamp: Date.now() },
      turnData: { movie: null, actor: null, lockedIn: false },
    }
    if (isWin) update.status = 'won'

    console.log(`[submitCoopTurn] writing currentTurn: ${otherKey} | isWin: ${isWin}`)
    tx.update(gameRef, update)
    return { isWin, hops: Math.floor(newChain.length / 2) }
  })
}

/**
 * Clear lastMove after the receiving player dismisses the reveal popup.
 */
export async function acknowledgeLastMove(gameId) {
  await updateDoc(doc(db, 'games', gameId), { lastMove: null })
}

/**
 * Mark a game as abandoned by one player.
 * Sets status: 'abandoned', abandonedBy: playerKey, abandonedAt: timestamp.
 * Used by both H2H and Co-Op quit flows. The other player's real-time
 * listener detects the status change and shows the abandoned screen.
 *
 * forfeitedAt0Hops: true  → quitter made 0 hops (game cancelled, both refunded)
 * forfeitedAt0Hops: false → quitter had progress (forfeit, only opponent refunded)
 * forfeitedAt0Hops: null  → Co-Op / legacy path (field omitted)
 */
export async function abandonGame(gameId, playerKey, forfeitedAt0Hops = null) {
  const update = {
    status: 'abandoned',
    abandonedBy: playerKey,
    abandonedAt: serverTimestamp(),
  }
  if (forfeitedAt0Hops !== null) {
    update.forfeitedAt0Hops = forfeitedAt0Hops
  }
  await updateDoc(doc(db, 'games', gameId), update)
}

/**
 * Atomically increment a player's cueCardsUsed counter in the game doc.
 * Called whenever an H2H player uses a Cue Card during their gameplay.
 * playerKey: 'p1' | 'p2'
 */
export async function incrementCueCardUsed(gameId, playerKey) {
  await updateDoc(doc(db, 'games', gameId), {
    [`${playerKey}.cueCardsUsed`]: increment(1),
  })
}

/**
 * Mark one player as having exited the results room.
 * Deletes the game document once both players have exited.
 */
export async function markPlayerExited(gameId, playerKey) {
  const gameRef = doc(db, 'games', gameId)
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(gameRef)
    if (!snap.exists()) return
    const data     = snap.data()
    const otherKey = playerKey === 'p1' ? 'p2' : 'p1'
    if (data.exited?.[otherKey]) {
      tx.delete(gameRef)
    } else {
      tx.update(gameRef, { [`exited.${playerKey}`]: true })
    }
  })
}
